// src/services/address/cep-external/factory.js
// *******************************************************************************
// CEP Provider Factory - Multi-provider with automatic fallback
// *******************************************************************************
// Factory chain: Provider 1 → Provider 2 → ...
// Based on backend pattern: src/modules/integrations/factory-helpers.js
// *******************************************************************************

import brasilapiAdapter from './brasilapi/adapter.js';
import viacepAdapter from './viacep/adapter.js';
import { createProviderFactory } from '../factory-helpers.js';

/**
 * CEP Provider registry
 * Order matters: First provider = highest priority
 * Default chain: ViaCEP → BrasilAPI
 */
const CEP_REGISTRY = {
  viacep: { name: 'viacep', adapter: viacepAdapter },
  brasilapi: { name: 'brasilapi', adapter: brasilapiAdapter },
};

/**
 * Creates CEP provider instance with automatic fallback
 *
 * @param {Object} [options] - Factory options
 * @param {string|Array<string>} [options.provider] - Provider(s) to use
 *   - If not specified: uses all providers in default order (ViaCEP → BrasilAPI)
 *   - If string: single provider or comma-separated ("brasilapi,viacep")
 *   - If array: ordered provider names (["viacep", "brasilapi"])
 *
 * @returns {Object} - Provider instance
 * @returns {Array<string>} return.chain - Active provider chain
 * @returns {Object} return.adapter - Composite adapter with fallback logic
 *
 * @example
 * // Use default chain (all providers)
 * const { adapter } = getCepProvider();
 * const result = await adapter.searchAddress({ cep: '13010111' });
 *
 * @example
 * // Use only specific provider
 * const { adapter } = getCepProvider({ provider: 'viacep' });
 *
 * @example
 * // Custom fallback order
 * const { adapter } = getCepProvider({ provider: ['viacep', 'brasilapi'] });
 */
export const getCepProvider = createProviderFactory({
  registry: CEP_REGISTRY,
  methods: ['searchAddress'],
});

export default {
  getCepProvider,
};
